<template>
	<div class="inner-page form-blocks">

		<template v-if="['admin'].includes($page.props.user.user_type)">

			<AppHead :title="block ? 'Edit Form Block ' : 'New Form Block'" />
			
			<PageHeader 
				:title="block ? 'Edit Form Block ' : 'New Form Block'"
				:desc="`Create a subset of questions within a block. The parent form is <strong>[${form.name}]</strong>`"
				:hasButton="false"
				:showHelper="false"
				:backButton="true"
				:backLocation="`/admin/pms-forms/${form.id}/blocks`"
				:reverseRightBlock="true"
				>

				<Button 
					label="Save"
					color="green"
					icon="save"
					:disabled="formData.name.length == 0"
					@click="saveFormBlock"
					/>

			</PageHeader>

			<InputGroup
				title="Reference name"
				desc="Please give the block a reference name. This will be used to identify the block in administration views only."
				>

				<InputText
					label="Block name"
					type="text"
					v-model="formData.name"
					autocomplete="disabled"
					/>

			</InputGroup>

			<InputGroup
				title="Priority"
				desc="Controls the order of the showing of the block in the form. Lower numbers will appear first, higher numbers will appear later in the form. As a rule, space out numbers in increments of 10 to allow for future blocks to fit inbetween."
				>

				<InputText
					label="Priority"
					type="number"
					v-model="formData.priority"
					autocomplete="disabled"
					/>

			</InputGroup>

			<InputGroup
				title="Block Status"
				desc="Enable or disable the block. If disabled, the block will not show up in the Post Market Surveillance form."
				>

				<InputOnOffPill
					label="Status"
					v-model="formData.active"
					/>

			</InputGroup>

			<InputGroup
				title="Show for all cases?"
				desc="Instead of using conditional logic, you can choose to show this block for all cases."
				>

				<InputOnOffPill
					label="Show for all cases"
					v-model="formData.showForAllCases"
					/>

			</InputGroup>

			<template v-if="!formData.showForAllCases">
				<InputGroup
					title="Technical File conditions"
					desc="Set the condition based on matching an Technical File."
					>

					<MultiSelect
						label="Technical Files"
						v-model="formData.technicalFiles"
						:options="technicalFiles"
						:showSelectedList="false"
						:enableSearch="false"
						/>

				</InputGroup>

				<InputGroup
					title="Anatomy conditions"
					desc="Set the condition based on matching an Anatomy."
					>

					<MultiSelect
						label="Anatomies"
						v-model="formData.anatomies"
						:options="anatomies"
						:showSelectedList="false"
						:enableSearch="false"
						/>

				</InputGroup>

				<InputGroup
					title="Case Features conditions"
					desc="Set the condition based on matching an Case Features."
					>

					<MultiSelect
						label="Case Features"
						v-model="formData.caseFeatures"
						:options="caseFeatures"
						:showSelectedList="false"
						:enableSearch="false"
						/>

				</InputGroup>

			</template>

			<InputGroup
				title="Available time points"
				desc="When should this block be available?"
				>

				<InputFancyRadio
					label="Time point mode"
					id="timePointMode"
					v-model="formData.timepoint.mode"
					:options="timepointOptions"
					/>

				<div 
					v-if="formData.timepoint.mode === 'time-intervals'"
					class="time-intervals-container"
					>

					<div 
						class="time-interval"
						v-for="(point, index) in formData.timepoint.points"
						:key="index"
						>
						<InputText
							label="Weeks since surgery"
							type="number"
							v-model="point.weeks"
							autocomplete="disabled"
							:noMargin="true"
							/>
						<Button
							label="Remove"
							color="red"
							size="medium"
							icon="trash"
							:forceHeight="true"
							@click="formData.timepoint.points.splice(index, 1)"
							/>
					</div>

					<Button
						label="Add time point"
						color="green"
						size="regular"
						icon="plus"
						@click="addTimePoint"
						/>
				</div>
				

			</InputGroup>
			
			
			<div class="form-creator-container">

				<div id="surveyCreator" />
				
				<pre>
					{{ formData }}
				</pre>

			</div>
			

		</template>
	
	</div>
</template>

<script>

import "survey-core/defaultV2.min.css"
import "survey-creator-core/survey-creator-core.min.css"
import { SurveyCreator } from "survey-creator-knockout"
import { setLicenseKey } from "survey-core"

import AppLayout from "@/Layouts/AppLayout.vue"
import AppHead from "@/Components/AppHead"
import PageHeader from "@/Components/PageHeader"
import Subtitle from "@/Components/Subtitle"
import Placeholder from "@/Components/Placeholder"
import Button from "@/Components/Button"
import { Link } from '@inertiajs/inertia-vue3'
import InputText from "@/Components/InputText"
import InputGroup from "@/Components/InputGroup"
import MultiSelect from "@/Components/MultiSelect"
import InputOnOffPill from "@/Components/InputOnOffPill"
import InputFancyRadio from "@/Components/InputFancyRadio"

export default {
	components: {
		AppLayout,
		AppHead,
		PageHeader,
		Subtitle,
		Placeholder,
		Button,
		Link,
		InputText,
		SurveyCreator,
		InputGroup,
		MultiSelect,
		InputOnOffPill,
		InputFancyRadio
	},
	layout: AppLayout,
	props: {
	  surveyJSKey: {
	    type: String,
	    required: true
	  },
		form: {
			type: [Object, Array, Boolean],
			default: false
		},
		block: {
			type: [Object, Array, Boolean],
			default: false
		},
		anatomies: {
			type: [Array, Boolean],
			default: false
		},
		caseFeatures: {
			type: [Array, Boolean],
			default: false
		},
		technicalFiles: {
			type: [Array, Boolean],
			default: false
		},
	},
	data: () => {
		return {
			creatorOptions: {
				showLogicTab: true,
				isAutoSave: true,
				allowModifyPages: false,
				pageEditMode: "single",
				showSurveyTitle: false,
				focusFirstQuestionAutomatic: false
			},
			defaultJson: {},
			formData: {
				name: '',
				active: false,
				showForAllCases: false,
				priority: 0,
				anatomies: [],
				caseFeatures: [],
				technicalFiles: [],
				timepoint: {
					mode: '',
					points: []
				},
				formBlockStructure: null
			},
			creator: null
		}
	},
	mounted(){

		console.log('form', this.form)
		console.log('block', this.block)

		// scroll to the top of the page
		this.$nextTick(() => {
			window.scrollTo(0, 0)
		})

		// fill in the form data if we are editing
		if(this.block){
			this.prefillForm()
		}

		this.initSurveyCreator()
		
	},
	methods: {
		addTimePoint(){
			this.formData.timepoint.points.push({
				weeks: 0
			})
		},
		saveFormBlock(){
			console.log('saving', this.formData)

			this.creator.saveSurvey()

			// stringify the timepoints in formdata
			// this.formData.timepoint = JSON.stringify(this.formData.timepoint)
			
			this.$nextTick(() => {
				if(!this.block){
					this.$inertia.post(`/admin/pms-forms/${this.form.id}/blocks/new`, this.formData)
				}else{
					this.$inertia.post(`/admin/pms-forms/${this.form.id}/blocks/${this.block.id}/edit`, this.formData)
				}
			})

		},
		prefillForm(){
			this.formData = {
				...this.block,
				anatomies: this.block.anatomies.map(a => a.id),
				caseFeatures: this.block.case_features.map(a => a.id),
				technicalFiles: this.block.technical_files.map(a => a.id),
				timepoint: this.block.timepoint ? this.block.timepoint : {
					mode: '',
					points: []
				},
				showForAllCases: this.block.show_for_all_cases
			}

			if(this.block.block_data != null){
				// if this.block.block_data is json
				try {
					this.defaultJson = JSON.parse(this.block.block_data)
				} catch (e) {
					this.defaultJson = this.block.block_data
				}
			}
		},
		initSurveyCreator(){
			// Survey stuff
			setLicenseKey(
				this.surveyJSKey
			)

			this.creator = new SurveyCreator(this.creatorOptions)

			// surveyJs by itself scrolls the page down to where it is when initialised, this scrollto reverses that rather unellagantly, but 90% of the time it works every time.
			this.$nextTick(() => {
				setTimeout(() => {
					window.scrollTo(0, 0)
				}, 100)
			})

	    this.creator.text = JSON.stringify(this.defaultJson)
			this.formData.formBlockStructure = this.creator.text

	    this.creator.saveSurveyFunc = (saveNo, callback) => { 
				console.log('saving survey', this.creator.text)

				this.formData.formBlockStructure = this.creator.text

	      callback(saveNo, true)
	    }

	    this.creator.render("surveyCreator")
		}
	},
	computed: {
		timepointOptions(){

			if(this.form.timepoint && this.form.timepoint.mode && ['once-after-surgery', 'once-before-pms'].includes(this.form.timepoint.mode)){
				return [
					{
						text: 'Always',
						desc: 'Always shows in the form. This is the only option available for this block because the form has been set to display once.',
						value: 'always'
					},
				]
			}else{
				return [
					{
						text: 'Always',
						desc: 'Always shows in the form.',
						value: 'always'
					},
					{
						text: 'At a custom time interval',
						desc: 'Define time intervals in weeks since surgey date, and the form block will be shown at those intervals.',
						value: 'time-intervals'
					}
				]
			}
		}
	}
};
</script>

<style lang="sass">

.form-blocks

	#surveyCreator
		height: 100vh
		width: 100%
		box-shadow: 0 0 10px rgba(#000, 0.1)

	pre
		white-space: pre-wrap
		word-wrap: break-word
		overflow: auto
		max-height: 700px
		border: 1px solid #ddd
		padding: 10px
		margin-top: 20px
		background: #f9f9f9
		border-radius: 5px
		box-shadow: 0 0 10px rgba(#000, 0.1)

	.time-intervals-container
		max-width: 300px
		width: 100%
		display: flex
		flex-direction: column
		gap: 10px

		.time-interval
			display: flex
			gap: 10px
			align-items: flex-end
		

</style>